import React, { useState, useEffect } from 'react';
import { api } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Search, Microscope, Sparkles, Plus, RefreshCw, Info } from 'lucide-react';
import { TrendCard } from './TrendCard';
import { HoldingsPanel } from './HoldingsPanel';
import { ResearchWatchlist } from './ResearchWatchlist';
import { ExplanationPanel } from './ExplanationPanel';

const STORAGE_KEY = 'sraivest_research_watchlist';
const FOLLOW_UPS = ['Compare reported position sizes', 'Read the latest company filing', 'Check how old the reported holdings are'];

const loadWatchlist = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; } catch { return []; }
};

export const MarketResearchSection = () => {
  const [ticker, setTicker] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [explaining, setExplaining] = useState(false);
  const [error, setError] = useState('');
  const [watchlist, setWatchlist] = useState(loadWatchlist);

  useEffect(() => { localStorage.setItem(STORAGE_KEY, JSON.stringify(watchlist)); }, [watchlist]);

  const run = async (sym, explain = false) => {
    const t = (sym || '').trim().toUpperCase();
    if (!t) return;
    setError('');
    explain ? setExplaining(true) : setLoading(true);
    try {
      const res = await api.post('/live-markets/research', { ticker: t, explain, question_id: 'explain_trend' });
      setData(res.data);
      setTicker(t);
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Enter a valid ticker symbol (e.g. AAPL).');
    } finally {
      setLoading(false); setExplaining(false);
    }
  };

  const addWatch = (type, value) => setWatchlist((w) => (w.some((i) => i.type === type && i.value === value) ? w : [...w, { type, value }]));
  const removeWatch = (it) => setWatchlist((w) => w.filter((i) => !(i.type === it.type && i.value === it.value)));

  return (
    <Card className="card-dashboard" data-testid="market-research-section">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2"><Microscope className="w-5 h-5 text-[#00E676]" /> SRAivest AI Market Research</CardTitle>
        <p className="text-xs text-gray-400 flex items-start gap-2" data-testid="research-notice">
          <Info className="w-4 h-4 shrink-0" />
          General market research. Reported holdings may be delayed and incomplete. Historical trends do not guarantee future results. This feature does not choose investments or execute trades.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); run(ticker); }}>
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input value={ticker} onChange={(e) => setTicker(e.target.value.slice(0, 8))} placeholder="Stock ticker, e.g. AAPL"
              className="input-dark pl-9" data-testid="research-ticker-input" />
          </div>
          <Button type="submit" disabled={loading} className="bg-[#00E676] text-black hover:bg-[#00E676]/90" data-testid="research-search-btn">
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Research'}
          </Button>
        </form>
        {error && <p className="text-sm text-red-400" data-testid="research-error">{error}</p>}

        {data && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-white font-semibold" data-testid="research-ticker-title">{data.ticker} price trend</h4>
                <Button variant="outline" size="sm" onClick={() => addWatch('ticker', data.ticker)} className="bg-white/5 border-white/10 text-white" data-testid="research-watch-ticker-btn">
                  <Plus className="w-3 h-3 mr-1" /> Watch
                </Button>
              </div>
              <TrendCard prices={data.prices} trend={data.trend} />
            </div>
            <HoldingsPanel holdings={data.holdings} onWatchManager={(m) => addWatch('manager', m)} />
          </div>
        )}

        {data && (
          <div className="space-y-3">
            <Button onClick={() => run(data.ticker, true)} disabled={explaining || !data.trend?.available}
              className="bg-white/10 text-white hover:bg-white/20" data-testid="research-explain-btn">
              {explaining ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Explain these facts
            </Button>
            {data.explanation?.reason !== 'Explanation not requested.' && <ExplanationPanel explanation={data.explanation} />}
            <div className="flex flex-wrap gap-2" data-testid="research-suggested-actions">
              {FOLLOW_UPS.map((q) => <span key={q} className="text-xs px-3 py-1 rounded-full border border-white/10 text-gray-400">{q}</span>)}
            </div>
          </div>
        )}

        <ResearchWatchlist items={watchlist} onSelect={run} onRemove={removeWatch} />
      </CardContent>
    </Card>
  );
};
